/* global gsap SplitText */

const {KitModule, registerModule} = window;

const mobileMediaQuery = window.matchMedia('(max-width: 999px)'); // nav break point
const prefersNoAnimation = window.matchMedia('(prefers-reduced-motion: reduce)');


// -------------------------- square gsap plugin -------------------------- //

/**
 * get current value for `square`
 * @param {SvgRect} target
 * @return {Object} - size, x, y, rotate
 */
function getCurrentSquareValue(target) {
  const size = gsap.getProperty(target, 'width');
  const transform = target.getAttribute('transform');
  let x = 0;
  let y = 0;
  let rotate = 0;
  if (transform) {
    const translateMatch = transform.match(/translate\((\d+\.?\d*),\s*(\d+\.?\d*)/);
    if (translateMatch) {
      x = parseFloat(translateMatch[1]);
      y = parseFloat(translateMatch[2]);
    }
    const rotateMatch = transform.match(/rotate\((\d+\.?\d*)\)/);
    if (rotateMatch) {
      rotate = parseFloat(rotateMatch[1]);
    }
  }

  return {x, y, rotate, size};
}

// add custom plugin for animating `square`
// @param {Object} - size, x, y, rotate
// used for rotating squares around their centerw
gsap.registerPlugin({
  name: 'square',
  get(target) {
    getCurrentSquareValue(target);
  },
  init(target, endValue) {
    const currentValue = getCurrentSquareValue(target);
    this.target = target;
    this.interp = {
      x: gsap.utils.interpolate(currentValue.x, endValue.x),
      y: gsap.utils.interpolate(currentValue.y, endValue.y),
      size: gsap.utils.interpolate(currentValue.size, endValue.size),
      rotate: gsap.utils.interpolate(currentValue.rotate, endValue.rotate),
    };
  },
  render(progress, data) {
    const size = data.interp.size(progress);
    const x = data.interp.x(progress);
    const y = data.interp.y(progress);
    const rotate = data.interp.rotate(progress);
    data.target.setAttribute('width', size);
    data.target.setAttribute('height', size);
    data.target.setAttribute('x', size * -0.5);
    data.target.setAttribute('y', size * -0.5);
    data.target.setAttribute('transform', `translate(${x}, ${y}) rotate(${rotate})`);
  },
});

// -------------------------- shapeGeometries -------------------------- //

const shapeGeometries = {
  0: {
    $circle1: {r: 256, x: 300, y: 300},
    $circle2: {r: 181, x: 300, y: 300},
    $circle3: {r: 75, x: 119, y: 481},
    $square1: {square: {size: 362, x: 300, y: 300, rotate: 0}},
    $square2: {square: {size: 238, x: 481, y: 300, rotate: 0}},
  },
  1: {
    $circle1: {r: 128, x: 300, y: 172},
    $circle2: {r: 128, x: 300, y: 428},
    $circle3: {r: 75, x: 300, y: 375},
    $square1: {square: {size: 362, x: 300, y: 300, rotate: 45}},
    $square2: {square: {size: 256, x: 428, y: 172, rotate: 0}},
  },
  2: {
    $circle1: {r: 181, x: 300, y: 300},
    $circle2: {r: 128, x: 172, y: 172},
    $circle3: {r: 128, x: 428, y: 428},
    $square1: {square: {size: 256, x: 300, y: 300, rotate: 0}},
    $square2: {square: {size: 128, x: 364, y: 364, rotate: 0}},
  },
  3: {
    $circle1: {r: 137, x: 300, y: 240},
    $circle2: {r: 137, x: 300, y: 433},
    $circle3: {r: 80, x: 412, y: 128},
    $square1: {square: {size: 386, x: 300, y: 240, rotate: 0}},
    $square2: {square: {size: 274, x: 300, y: 240, rotate: 45}},
  },
};

// -------------------------- HeroHome -------------------------- //

class HeroHome extends KitModule {
  constructor($element) {
    super($element);

    if (prefersNoAnimation.matches) return;

    // other elements
    this.$linkDeluxeItems = [...this.$element.querySelectorAll('.linkDeluxe__item')];
    // properties
    this.groupDelays = this.$illoGroups.map(($group) => {
      return parseFloat($group.getAttribute('data-delay'));
    });
    this.geometryIndex = 0;

    this.animateIntro();

    // if viewport is resized from desktop to mobile
    // remove mousemove animation from svg
    mobileMediaQuery.addEventListener('change', (event) => {
      if (event.matches) {
        // we get this.$element thanks to KitModule, super(), and registerModule
        // this element the element with data-js-module="HeroHome"
        // passing in `this` is another KitModule feature
        // it will trigger the matching `onmousemove` method
        this.$element.removeEventListener('mousemove', this);
      } else {
        this.$element.addEventListener('mousemove', this);
      }
    });
  }

  animateIntro() {
    // this.$heroTitle is generated from setting data-js-child on a single child element
    const childSplit = new SplitText(this.$heroTitle, {type: 'lines', linesClass: 'split-child'});
    new SplitText(this.$heroTitle, {type: 'lines', linesClass: 'split-parent'});

    gsap.set('.hero-home__cta a', {opacity: 0});
    gsap.set(this.$heroEyebrow, {opacity: 0});
    // this.$illoGroups is generated from setting data-js-children on multiple child elements
    gsap.set(this.$illoGroups, {opacity: 0});

    const tl = gsap.timeline({paused: true});

    tl.to(this.$heroTitle, {opacity: 1, duration: 0.1, ease: 'power2.out'});
    tl.to(this.$heroEyebrow, {opacity: 1, duration: 0.1, ease: 'power2.out'});
    tl.fromTo(childSplit.lines, {y: 100, opacity: 0}, {y: 0, opacity: 1, duration: 0.9, ease: 'power2.out'});
    tl.to('.hero-home__cta a', {opacity: 1, stagger: 0.1, duration: 1.2, ease: 'power2.in'}, '-=0.7');
    tl.to(this.$illoGroups, {opacity: 1, duration: .5, stagger: 0.13, ease: 'sine.inOut'}, '-=1');
    tl.fromTo(this.$illoGroups, {x: -20, y: 10}, {x: 0, y: 0, stagger: 0.1, duration: 1, ease: 'sine.in'}, '-=1.7');
    tl.eventCallback('onComplete', () => {
      // if on mobile viewport, don't add mousemove
      // animation to svg
      if (mobileMediaQuery.matches) return;

      this.$element.addEventListener('mousemove', this);
    });

    tl.play();
  }

  onmousemove(event) {
    // set shape geometry
    const $linkDeluxeItem = event.target.closest('.linkDeluxe__item');
    if ($linkDeluxeItem) {
      const itemIndex = this.$linkDeluxeItems.indexOf($linkDeluxeItem);
      this.setGeometryIndex(itemIndex + 1);
    } else {
      this.setGeometryIndex(0);
    }

    // hover effect
    const moveX = (event.pageX - window.innerWidth / 2);
    const moveY = (event.pageY - window.innerHeight / 2);

    this.$illoGroups.forEach((path, i) => {
      gsap.to(path, {
        x: moveX * 0.2,
        y: moveY * 0.2,
        duration: this.groupDelays[i],
        ease: 'power2.out',
      });
    });
  }

  setGeometryIndex(index) {
    if (index === this.geometryIndex) return;
    this.geometryIndex = index;
    const shapeGeometry = shapeGeometries[this.geometryIndex];
    Object.keys(shapeGeometry).forEach((key, i) => {
      gsap.to(this[key], {
        ...shapeGeometry[key],
        duration: (0.3 + i * 0.15),
        ease: 'power2.out',
      });
    });
  }
}

// registerModule takes care of initializing the class
// on elements that match the string here, 'HeroHome'
registerModule('HeroHome', HeroHome);
